import { useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { toDate, toZonedTime } from 'date-fns-tz';
import isEmail from 'validator/lib/isEmail';
import api from 'api';
import { getMessageWithPlaceholders } from 'shared-locales';
import {
  attributesConditionsMatch,
  canUserCustomAttributeBeEdited,
  canUserCustomAttributeBeViewed,
  getApplicableUserCustomAttributes
} from 'shared-helpers/customAttributes';
import {
  decodeCustomAttributeKeyForForm,
  encodeCustomAttributeKeyForForm,
  unsetRemovedUserCustomAttributes
} from 'lib/customAttributes';
import { Datepicker, FormGroup, Text } from 'components/common/form';
import { Alert, ModalButtonWithForm } from 'components/common';
import { UserCustomAttribute } from 'components/user';

import type { FormState } from 'informed';
import type { User, UserCustomAttributes } from 'shared-types';

type FormValues = Pick<
  User,
  | 'customAttributes'
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'username'
  | 'expirationDate'
>;

interface Props {
  buttonIsDropdown?: boolean;
  handleSuccess: () => Promise<void>;
  modalButtonWithFormProps?: any;
  targetUser: FormValues & Pick<User, 'id' | 'isSelfRegistered' | 'roles'>;
}

const UsersEdit = ({
  buttonIsDropdown,
  handleSuccess,
  modalButtonWithFormProps,
  targetUser
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: {
      accountEnableUsernameAccounts,
      accountExpirationDeleteBufferInDays,
      timeZone,
      userCustomAttributes,
      userUseName,
      userUseNameInCertificate
    },
    user
  } = useReduxState();

  const [renderForm, setRenderForm] = useState(false);

  const validateEmail = async (untrimmedValue: string) => {
    const value = untrimmedValue?.trim();
    if (!value && accountEnableUsernameAccounts) return undefined;
    if (!value) return messages.authEmailEmptyError;
    if (!isEmail(value, { domain_specific_validation: true })) {
      return messages.usersInvalidEmail;
    }

    try {
      await api.head(
        `/admin/users?email=${encodeURIComponent(value)}&id=ne|${targetUser.id}`
      );
      return messages.authEmailInUseError;
    } catch (err) {
      return undefined;
    }
  };

  const validateUsername = async (untrimmedValue: string) => {
    const value = untrimmedValue?.trim();
    if (!value) return messages.authUsernameEmptyError;
    if (isEmail(value)) return messages.authInvalidEmailAsUsername;

    try {
      await api.head(`/admin/users?username=${value}&id=ne|${targetUser.id}`);
      return messages.authUsernameInUseError;
    } catch (err) {
      return undefined;
    }
  };

  const submit = async (values: FormValues) => {
    if (accountEnableUsernameAccounts && !values.email) {
      if (targetUser.email) values.email = null;
    }

    const data = {
      customAttributes: null,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      username: values.username,
      expirationDate: values.expirationDate
        ? toDate(values.expirationDate, { timeZone })
        : null
    };

    if (values.customAttributes) {
      const decodedCustomAttributes = Object.fromEntries(
        Object.entries(values.customAttributes).map(([key, value]) => [
          decodeCustomAttributeKeyForForm(key),
          value
        ])
      );

      const customAttributes = unsetRemovedUserCustomAttributes({
        reqUser: user,
        userCustomAttributes,
        userCustomAttributeValues: decodedCustomAttributes
      });

      data.customAttributes = {
        ...targetUser.customAttributes,
        ...customAttributes
      };
    } else delete data.customAttributes;

    await api.put(`/admin/users/${targetUser.id}`, data);

    await handleSuccess();

    dispatch.addNotice({
      text: messages.usersEditUserSuccess,
      type: 'success'
    });
  };

  const {
    id,
    email,
    firstName,
    lastName,
    username,
    expirationDate,
    isSelfRegistered
  } = targetUser;

  const initialValues = {
    customAttributes: {},
    email,
    firstName,
    isSelfRegistered,
    lastName,
    username,
    expirationDate: expirationDate
      ? toZonedTime(expirationDate, timeZone).getTime()
      : null
  };

  const applicableUserCustomAttributes = getApplicableUserCustomAttributes(
    targetUser,
    userCustomAttributes
  ).filter((attribute) =>
    canUserCustomAttributeBeViewed({ user, userCustomAttribute: attribute })
  );

  if (targetUser?.customAttributes) {
    for (const key in targetUser.customAttributes) {
      const attr = applicableUserCustomAttributes.find(
        (attr) => attr.key === key
      );
      if (attr) {
        initialValues.customAttributes[encodeCustomAttributeKeyForForm(key)] =
          targetUser.customAttributes[key];
      }
    }
  }

  const userIsAdmin = user?.roles.includes('admin');
  const userIsSuperadmin = user?.roles.includes('superadmin');

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="edit"
      buttonIsDropdown={buttonIsDropdown}
      buttonText={messages.commonEdit}
      buttonDisabled={!userIsSuperadmin && targetUser.roles.includes('admin')}
      formAllowEmptyStrings={true}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSaveChanges}
      modalTitle={messages.usersEditUser}
      modalWidth="medium"
      onHide={() => setRenderForm(false)}
      onShow={() => setRenderForm(true)}
      onSubmit={submit}
      pristineIsValid={false}
      stopPropagation={true}
      {...modalButtonWithFormProps}
    >
      {(formState: FormState<FormValues>) =>
        !renderForm ? null : (
          <>
            {formState.values.expirationDate ? (
              <Alert
                text={getMessageWithPlaceholders({
                  message: messages.userExpireAccount,
                  placeholders: {
                    numberOfDays: accountExpirationDeleteBufferInDays
                  }
                })}
                type="warning-light"
              />
            ) : null}

            <FormGroup
              htmlFor={`email-${id}`}
              label={messages.commonEmail}
              maxWidth="320px"
            >
              <Text
                customValidateAsync={validateEmail}
                field="email"
                id={`email-${id}`}
              />
            </FormGroup>

            {!userUseName ||
            userIsSuperadmin ||
            (userIsAdmin && user?.permissions.includes('manage-users')) ? (
              <FormGroup
                htmlFor={`username-${id}`}
                label={messages.commonUsername}
                maxWidth="320px"
              >
                <Text
                  customValidateAsync={validateUsername}
                  field="username"
                  id={`username-${id}`}
                />
              </FormGroup>
            ) : null}

            {userUseName || userUseNameInCertificate || userIsSuperadmin ? (
              <FormGroup>
                <div className="row-sm">
                  <div className="col-1of2">
                    <FormGroup
                      htmlFor={`first-name-${id}`}
                      label={messages.usersFirstName}
                      maxWidth="320px"
                    >
                      <Text field="firstName" id={`first-name-${id}`} />
                    </FormGroup>
                  </div>
                  <div className="col-1of2">
                    <FormGroup
                      htmlFor={`last-name-${id}`}
                      label={messages.usersLastName}
                      maxWidth="320px"
                    >
                      <Text field="lastName" id={`last-name-${id}`} />
                    </FormGroup>
                  </div>
                </div>
              </FormGroup>
            ) : null}

            <FormGroup label={messages.commonExpirationDate}>
              <Datepicker
                field="expirationDate"
                id={`expiration-date-${id}`}
                isClearable={true}
                minDate={dateNow}
                placeholder={messages.commonExpirationDate}
                style={{ width: 200 }}
              />
            </FormGroup>

            {applicableUserCustomAttributes.length
              ? applicableUserCustomAttributes.map((attribute, i) => {
                  const customAttributes = Object.fromEntries(
                    Object.entries(
                      formState.values?.customAttributes || {}
                    ).map(([key, value]) => [
                      decodeCustomAttributeKeyForForm(key),
                      value
                    ])
                  ) as UserCustomAttributes;

                  const conditionsMatch = attributesConditionsMatch({
                    conditions: attribute.conditions,
                    customAttributeValues: customAttributes,
                    user: targetUser
                  });
                  if (!conditionsMatch) return null;

                  const isReadOnly = !canUserCustomAttributeBeEdited({
                    user,
                    userCustomAttribute: attribute
                  });

                  const isRequired = attribute.isRequired?.value
                    ? attributesConditionsMatch({
                        conditions: attribute.isRequired?.conditions || [],
                        customAttributeValues: customAttributes,
                        isRequiredCheck: true,
                        user: {
                          email: formState.values?.email?.trim(),
                          isSelfRegistered: targetUser.isSelfRegistered
                        }
                      })
                    : false;

                  const error =
                    formState.errors.customAttributes &&
                    formState.errors.customAttributes[
                      encodeCustomAttributeKeyForForm(attribute.key)
                    ];

                  return (
                    <UserCustomAttribute
                      attribute={attribute}
                      calledFrom="users"
                      customAttributeOriginalValues={
                        targetUser.customAttributes
                      }
                      customAttributeValues={{
                        ...targetUser.customAttributes,
                        ...formState.values?.customAttributes
                      }}
                      error={error}
                      id={id}
                      isAttributeRequired={isRequired}
                      isReadOnly={isReadOnly}
                      key={`${attribute.key}-${i}-${isRequired}`}
                      maxWidth={attribute.type === 'String' ? '320px' : null}
                    />
                  );
                })
              : null}
          </>
        )
      }
    </ModalButtonWithForm>
  );
};

export default UsersEdit;
